///
/// EarthBrowser
///
/// Ground Focus Adapter - Selection without a modal camera
///
/// Used by ground features (cities, locations) whose selection should frame
/// the feature's real-world extent and then hand camera control straight back
/// to standard Earth navigation. The feature keeps FocusManager focus (info
/// panel, callout, history, Escape/click-away dismissal all still work), but
/// there is no entity-orbit camera mode: pan/zoom/rotate after the transition
/// are the same globe gestures the user already knows. Navigating well beyond
/// the framed extent auto-dismisses the selection.
///
/// A direct tap on the feature zooms at most cMaxTapZoomRatio closer per
/// click: from a global view a city click lands on a regional view with the
/// city centered, rather than lurching from orbit to street scale in a
/// second. Programmatic focus (search, deep links, saved views) frames the
/// full extent, since there the user asked to be taken to the place.
///
/// Created by Matt Giger on Sep 3, 2026
/// Copyright (c) 2026 Geodata Labs
///

import { Constants } from "@/constants"
import { EntityHoverHandler } from "@/earthbrowser/datasets/EntityHoverHandler"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import type { Camera } from "@stovokor/camera/Camera"
import { CameraState } from "@stovokor/camera/CameraState"
import { cCameraFOV, Perspective } from "@stovokor/camera/Perspective"
import type { FocusTarget } from "@stovokor/gestures/FocusTarget"
import { type GestureNode } from "@stovokor/gestures/GestureNode"
import { TapGestureRecognizer } from "@stovokor/gestures/TapGestureRecognizer"
import { clamp, cross, dot, normal, trig, vec3 } from "@stovokor/math"

/** Fraction of the limiting screen half-extent the framing radius should fill */
const cFramingFill = 0.65

/** Most a single tap may shrink the camera-to-feature distance by */
const cMaxTapZoomRatio = 30

/** Extra flight time per doubling/halving of camera distance (seconds) */
const cDurationPerDistanceOctave = 0.1

/** Ceiling on the preserved camera tilt when framing (radians) - keeps the
 *  framing altitude sane when the user is looking near the horizon */
const cMaxFramingTilt = 60 * trig.rad

/** Auto-dismiss selection when the camera moves this many framing distances away */
const cDismissFactor = 5

/** Floor for the auto-dismiss distance (km), matching the old 100 km entity release */
const cMinDismissDistance = 100

/**
 * Approximate a populated place's visual extent (km) from its population.
 * Tuned so a ~10k town frames at a few km and a multi-million metro at
 * tens of km (e.g. 300k -> ~8 km, 8M -> ~43 km).
 */
export function populationFramingRadius(population: number | undefined, fallbackKm: number): number {
	if (population && population > 0) {
		return clamp(0.015 * Math.sqrt(population), 2.5, 60)
	}
	return fallbackKm
}

export class GroundFocusAdapter {
	private context: EarthBrowserContext
	private entity: GestureNode & FocusTarget
	private targetPosition: vec3
	private framingRadiusKm: number
	private camera?: Camera
	private framingDistance?: number
	// Set only for the synchronous span of a tap's requestFocus() call, so
	// the framing computed inside it applies the per-click zoom cap
	private tapFocusPending = false

	constructor(context: EarthBrowserContext, entity: GestureNode & FocusTarget, targetPosition: vec3, framingRadiusKm: number) {
		this.context = context
		this.entity = entity
		this.targetPosition = targetPosition
		this.framingRadiusKm = framingRadiusKm

		// Tap requests focus with the per-click zoom cap, hover shows the
		// preview callout. Ground features sit between the globe (tap 10)
		// and dataset entities (tap 20, hover 5) in gesture priority:
		// tapping a city still beats bare-Earth taps, but dataset markers
		// (webcams, buoys, ...) win when they overlap a city
		const tap = new TapGestureRecognizer({ maximumTouches: 1 })
		tap.priority = 15
		tap.onGesture(() => {
			// Restricted contexts (embeds) limit which entities a tap may focus
			if (context.canFocusEntity(entity)) {
				this.requestTapFocus()
			}
		})
		entity.addRecognizer(tap)
		entity.addRecognizer(new EntityHoverHandler(context, 2))
	}

	/**
	 * Focus the feature from a direct tap: frames it with the per-click zoom
	 * cap rather than the full extent. Label panes and other tap surfaces
	 * that stand in for the feature should route through here.
	 */
	public requestTapFocus(): boolean {
		this.tapFocusPending = true
		try {
			// focusTransition() runs synchronously inside requestFocus(), so
			// the flag is consumed before this returns
			return this.context.focusManager.requestFocus(this.entity)
		} finally {
			this.tapFocusPending = false
		}
	}

	public setFramingRadius(radiusKm: number): void {
		this.framingRadiusKm = radiusKm
	}

	/** Retarget the framing (e.g. focusing one member of a placemark) */
	public setTargetPosition(position: vec3): void {
		this.targetPosition = position
	}

	/** The Earth focus target (default focus) that owns standard navigation */
	private get earthTarget(): FocusTarget | undefined {
		return this.context.focusManager.getDefaultFocus()
	}

	// ========== Focus lifecycle (delegated to Earth navigation) ==========

	public attach(camera: Camera): void {
		this.camera = camera
		this.earthTarget?.onFocusGained?.(camera)
	}

	public update(): void {
		this.earthTarget?.onFocusUpdate?.()

		// Auto-dismiss the selection once the user navigates well beyond the
		// framed extent (mirrors the old entity-camera max-zoom-out release).
		// preserveCurrentState keeps the camera exactly where it is - only the
		// selection (info panel, callout) is dismissed.
		if (this.camera) {
			const distance = this.camera.position.subtract(this.targetPosition).length
			if (distance > this.dismissDistance()) {
				this.context.focusManager.releaseFocus({ preserveCurrentState: true })
			}
		}
	}

	public detach(): void {
		this.camera = undefined
		this.earthTarget?.onFocusLost?.()
	}

	public handleCommand(command: string, data?: unknown): boolean {
		return this.earthTarget?.handleCommand?.(command, data) ?? false
	}

	/**
	 * Distance from the feature beyond which the selection auto-dismisses,
	 * scaled to the framed extent so metros survive a wider zoom-out than towns
	 */
	private dismissDistance(): number {
		// Fallback covers focus paths that skipped the framing transition
		const framing = this.framingDistance ?? this.fullFramingDistance()
		return Math.max(framing * cDismissFactor, cMinDismissDistance)
	}

	/**
	 * Horizontal compass direction the current view faces, expressed in the
	 * tangent plane at the target. Near nadir the forward vector has no usable
	 * horizontal component, so the screen-up vector (which carries the compass
	 * bearing in a top-down view) is used instead - this is what keeps a
	 * north-up 2D view north-up through the framing transition.
	 */
	private currentHeading(camera: Camera, camUp: vec3, localUp: vec3): vec3 {
		const forwardNow = camera.state.forward
		let headingSource = forwardNow.subtract(camUp.multiply(dot(forwardNow, camUp)))
		if (headingSource.length < 1e-4) {
			const upNow = camera.state.up
			headingSource = upNow.subtract(camUp.multiply(dot(upNow, camUp)))
		}

		// Re-project onto the tangent plane at the target
		const heading = headingSource.subtract(localUp.multiply(dot(headingSource, localUp)))
		if (heading.length > 1e-6) {
			return normal(heading)
		}

		// Degenerate (e.g. at the poles): fall back to a north-up view
		const north = vec3.unitY.subtract(localUp.multiply(dot(vec3.unitY, localUp)))
		return north.length > 1e-6 ? normal(north) : normal(cross(localUp, vec3.unitZ))
	}

	// ========== Framing ==========

	/**
	 * Transition duration scaled by how far the camera travels - great-circle
	 * angle plus the change in camera-to-feature distance - so nearby
	 * selections feel snappy and big zooms or cross-globe flights don't
	 * lurch past everything in a fixed second. Call after
	 * getTargetCameraState() so the distance term sees the framing it chose.
	 */
	public transitionDuration(camera: Camera): number {
		const angle = Math.acos(clamp(dot(normal(camera.position), normal(this.targetPosition)), -1, 1))
		const currentDistance = camera.position.subtract(this.targetPosition).length
		const landingDistance = this.framingDistance ?? this.fullFramingDistance()
		const octaves = Math.abs(Math.log2(Math.max(currentDistance, 1e-3) / Math.max(landingDistance, 1e-3)))
		return clamp(0.8 + angle * 0.6 + octaves * cDurationPerDistanceOctave, 0.8, 2.6)
	}

	/**
	 * Camera distance the focus flight lands at (km): the last computed
	 * framing, or the full-extent framing before any transition ran
	 */
	public get landingDistance(): number {
		return this.framingDistance ?? this.fullFramingDistance()
	}

	/** Camera distance at which the framing radius fills cFramingFill of the default FOV */
	private fullFramingDistance(): number {
		return this.framingRadiusKm / (cFramingFill * Math.tan(cCameraFOV * 0.5 * trig.rad))
	}

	/**
	 * Compute the camera state that frames the feature's extent.
	 *
	 * The camera keeps the user's current orientation - same compass bearing,
	 * same tilt from nadir (capped at cMaxFramingTilt) - and re-aims that view
	 * at the feature, far enough away that the framing radius fills
	 * cFramingFill of the limiting screen dimension. A north-up top-down "2D"
	 * view therefore stays north-up and top-down instead of being kicked into
	 * an arbitrary oblique orbit.
	 *
	 * Tap focus (requestTapFocus) stops short of the full framing when that
	 * would zoom more than cMaxTapZoomRatio in one step; the user can keep
	 * zooming with the usual globe gestures.
	 */
	public getTargetCameraState(camera: Camera): CameraState {
		const target = this.targetPosition
		const localUp = normal(target)

		// Half-angle of the limiting (narrower) screen dimension
		let tanHalf = Math.tan(cCameraFOV * 0.5 * trig.rad)
		if (camera.projection instanceof Perspective) {
			const p = camera.projection
			const aspect = p.height > 0 ? p.width / p.height : 1
			tanHalf = Math.tan(p.fov * 0.5 * trig.rad) * Math.min(1, aspect)
		}
		let framingDistance = this.framingRadiusKm / (cFramingFill * tanHalf)
		if (this.tapFocusPending) {
			const currentDistance = camera.position.subtract(target).length
			framingDistance = Math.max(framingDistance, currentDistance / cMaxTapZoomRatio)
		}

		// Current view tilt from nadir, capped so the framing altitude stays sane
		const camUp = normal(camera.position)
		const cosTiltNow = clamp(-dot(camera.state.forward, camUp), -1, 1)
		const tilt = Math.min(Math.acos(cosTiltNow), cMaxFramingTilt)

		// Horizontal direction the view faces, in the target's tangent plane
		const heading = this.currentHeading(camera, camUp, localUp)

		// Keep the camera above the Earth navigation altitude floor so the
		// camera state survives activation unclamped
		const minDistance = (Constants.cMinCameraAltitude + 2) / Math.cos(tilt)
		const distance = Math.max(framingDistance, minDistance)
		this.framingDistance = distance

		// Back off from the target opposite the view heading, looking down at
		// the preserved tilt. At tilt 0 this degenerates gracefully: position
		// directly overhead, forward straight down, up along the heading.
		const sinTilt = Math.sin(tilt)
		const cosTilt = Math.cos(tilt)
		const position = target
			.add(localUp.multiply(distance * cosTilt))
			.add(heading.multiply(-distance * sinTilt))
		const forward = normal(heading.multiply(sinTilt).subtract(localUp.multiply(cosTilt)))
		const up = normal(heading.multiply(cosTilt).add(localUp.multiply(sinTilt)))

		return new CameraState(position, forward, up)
	}
}
